import React, { createContext, useContext, useState, useCallback } from 'react';

// Create a context for the dashboard
const DashboardContext = createContext();

// Custom hook to use the DashboardContext in other components
export const useDashboard = () => {
  return useContext(DashboardContext);
};

// Context provider component
// NOTE: This context now stores minimal state for performance.
// Time events are fetched on-demand from the server with pagination/filtering.
// Only individual event updates are kept in the cache for real-time updates.
export const DashboardProvider = ({ children }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  
  // Lightweight cache for individual event updates (not all events)
  // Only stores events that have been updated via WebSocket messages
  const [eventCache, setEventCache] = useState(new Map());
  
  // Legacy support: Keep these functions for backward compatibility
  // but they now only update the cache, not the full event list
  const setTimeEvents = useCallback((newEvents) => {
    // For backward compatibility with components that still use this
    // Now only caches these events instead of storing all
    if (Array.isArray(newEvents)) {
      setEventCache(prev => {
        const updated = new Map(prev);
        newEvents.forEach(event => {
          if (!event || typeof event.id === 'undefined') return;
          const eventId = parseInt(String(event.id), 10);
          updated.set(eventId, {...event, id: eventId});
        });
        return updated;
      });
    } else {
      console.warn('setTimeEvents received non-array value:', newEvents);
    }
  }, []);
  
  // Update individual event in cache (for real-time updates via WebSocket)
  const setTimeEvent = useCallback((event) => {
    console.log("Updating event in cache:", event);
    if (!event || typeof event.id === 'undefined') {
      console.error("Invalid event provided to setTimeEvent:", event);
      return;
    }
    
    const eventId = parseInt(String(event.id), 10);
    const normalizedEvent = {...event, id: eventId};
  
    setEventCache((prevCache) => {
      const updatedCache = new Map(prevCache);
      updatedCache.set(eventId, { ...updatedCache.get(eventId), ...normalizedEvent });
      return updatedCache;
    });
  }, []);
  
  // Get event from cache (for real-time updates)
  const getTimeEvent = useCallback((id) => {
    // Extract numeric ID from formats like "[EventID:1370]" or "1370"
    let extractedId = id;
    if (typeof id === 'string') {
      const match = id.match(/\[EventID:(\d+)\]/);
      if (match) {
        extractedId = parseInt(match[1], 10);
      } else {
        extractedId = parseInt(id, 10);
      }
    }
    
    const numId = typeof extractedId === 'string' ? parseInt(extractedId, 10) : extractedId;
    const strId = String(extractedId);
    
    // Try to find the event in cache
    let te = eventCache.get(numId);
    
    if (!te) {
      te = eventCache.get(strId);
    }
    
    if (!te) {
      for (let [key, value] of eventCache) {
        if (String(key) === strId || key === numId) {
          te = value;
          break;
        }
      }
    }
    
    return te || null;
  }, [eventCache]);

  // Delete event from cache
  const deleteTimeEvent = useCallback((id) => {
    const eventId = parseInt(String(id), 10);
    setEventCache((prevCache) => {
      const updatedCache = new Map(prevCache);
      updatedCache.delete(eventId);
      return updatedCache;
    });
  }, []);

  // Clear event cache (useful when switching cases)
  const clearEventCache = useCallback(() => {
    setEventCache(new Map());
  }, []);

  // Legacy support: Provide empty array for components expecting timeevents array
  // Components should migrate to server-side pagination instead
  const timeevents = [];
  const [filteredTimeEvents, setFilteredTimeEvents] = useState([]);

  return (
    <DashboardContext.Provider value={{
      title,
      setTitle,
      description,
      setDescription,
      timeevents, // Empty array - use server-side pagination instead
      setTimeEvents, // Legacy support - now only caches
      setTimeEvent,
      getTimeEvent,
      deleteTimeEvent,
      clearEventCache,
      eventCache, // Direct access to cache for components that need it
      filteredTimeEvents, // Legacy support - may be deprecated
      setFilteredTimeEvents,
      tags,
      setTags
    }}>
      {children}
    </DashboardContext.Provider>
  );
};
